import React, { useState } from "react";
import { CheckCircle2, AlertTriangle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { errorMessage, formsApi } from "../../lib/api";
import { EMAIL_RE } from "../../lib/forms";

/** Shows whether Resend is connected and lets the admin fire a test email. */
const EmailStatusCard = ({ status }) => {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  const sendTest = async () => {
    if (!EMAIL_RE.test(to)) return toast.error("Enter a valid email address");
    setBusy(true);
    try {
      await formsApi.testEmail(to);
      toast.success("Test email sent", { description: `Check the inbox for ${to}` });
    } catch (err) {
      toast.error(errorMessage(err, "Test email failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  const ok = status.configured;
  const sandbox = ok && status.sandbox;
  const tone = !ok || sandbox ? "border-amber-500/40 bg-amber-500/5" : "border-emerald-500/40 bg-emerald-500/5";
  return (
    <section className={`flex flex-col gap-4 border p-5 lg:flex-row lg:items-center lg:justify-between ${tone}`} data-testid="email-status-card">
      <div className="flex items-start gap-3">
        {ok && !sandbox ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" /> : <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />}
        <div>
          <p className="text-sm font-semibold" data-testid="email-status-text">
            {!ok ? "Email delivery is not connected yet" : sandbox ? "Email is connected — sandbox sender only" : "Email delivery is on (Resend)"}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {!ok ? (
              <>Submissions are saved in the inbox, but notifications and auto-replies are only logged. Add your Resend API key (RESEND_API_KEY) and a verified sending address (SENDER_EMAIL) to turn sending on.</>
            ) : sandbox ? (
              <>Sending as <span className="text-foreground">{status.sender}</span>. Team notifications to your own address work, but auto-replies to customers will fail until you verify a sending domain below.</>
            ) : (
              <>Sending as <span className="text-foreground">{status.sender}</span>. Submissions are still saved here even if an email fails.</>
            )}
          </p>
        </div>
      </div>
      {ok && (
        <div className="flex w-full gap-2 lg:w-auto">
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com" className="h-10 w-full rounded-none border-border bg-transparent text-sm lg:w-64" data-testid="test-email-to" />
          <Button onClick={sendTest} disabled={busy} variant="outline" className="h-10 shrink-0 rounded-none text-[11px] font-semibold uppercase tracking-[0.18em]" data-testid="test-email-send">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send test
          </Button>
        </div>
      )}
    </section>
  );
};

export default EmailStatusCard;
